import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import api from '../utils/api';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user, isAuthenticated, notificationPreferences } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isPolling, setIsPolling] = useState(false);

  // Fetch unread count
  const fetchUnreadCount = async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    
    if (notificationPreferences?.chatMessages === false) {
      setUnreadCount(0);
      setLoading(false);
      return;
    }
    
    try {
      // Use relative path; axios interceptor will prefix with /api
      const response = await api.get('/api/chat/unread/count');
      setUnreadCount(response.data.unreadCount);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    } finally {
      setLoading(false);
    }
  };

  // Add notification
  const addNotification = (notification) => {
    setNotifications(prev => [notification, ...prev.slice(0, 9)]); // Keep only last 10
  };

  // Mark notification as read
  const markAsRead = (notificationId) => {
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === notificationId 
          ? { ...notif, isRead: true }
          : notif
      )
    );
  };

  // Clear all notifications
  const clearAll = () => {
    setNotifications([]);
  };

  // Start polling for notifications
  const startPolling = () => {
    if (isPolling) return;
    
    setIsPolling(true);
    const interval = setInterval(async () => {
      if (isAuthenticated) {
        await fetchUnreadCount();
      }
    }, 10000); // Poll every 10 seconds

    return () => clearInterval(interval);
  };

  // Effects
  useEffect(() => {
    if (isAuthenticated) {
      fetchUnreadCount();
      const cleanup = startPolling();
      return cleanup;
    }
  }, [isAuthenticated, notificationPreferences?.chatMessages]);

  // Show browser notification for new messages
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'granted' && unreadCount > 0) {
      // Only show notification if user is not actively using the app
      if (document.hidden) {
        new Notification('New Messages', {
          body: `You have ${unreadCount} unread message${unreadCount > 1 ? 's' : ''}`,
          icon: '/favicon.ico',
          tag: 'chat-notification'
        });
      }
    }
  }, [unreadCount]);

  const value = {
    notifications,
    unreadCount,
    addNotification,
    markAsRead,
    clearAll,
    fetchUnreadCount,
    loading
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
